import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Article, Category } from "@/lib/supabase";
import { Button } from "@/components/ui/button";

interface ArticleCardProps {
  image: string;
  category: string;
  title: string;
  excerpt: string;
  path: string;
  delay: number;
}

interface FeaturedArticlesProps {
  articles: Article[];
  isLoading: boolean;
  categoryMap?: Record<string, string>;
}

const ArticleCard = ({
  image,
  category,
  title,
  excerpt,
  path,
  delay,
}: ArticleCardProps) => (
  <article
    className="group overflow-hidden rounded-xl glass-card hover-lift animate-fade-in opacity-0"
    style={{ animationDelay: `${delay}s` }}
  >
    <Link to={path} className="block">
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6">
        <div className="inline-block py-1 px-2 bg-navy/10 text-navy text-xs font-medium rounded-md mb-3">
          {category}
        </div>
        <h3 className="text-lg font-bold text-navy mb-2 group-hover:text-gold transition-colors duration-300">
          {title}
        </h3>
        <p className="text-navy/70 text-sm line-clamp-2 mb-4">{excerpt}</p>
        <div className="inline-flex items-center text-navy font-medium text-sm group-hover:text-gold transition-colors duration-300">
          <span>Read more</span>
          <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  </article>
);

const FeaturedArticles = ({
  articles,
  isLoading,
  categoryMap = {},
}: FeaturedArticlesProps) => {
  // Debug để kiểm tra dữ liệu articles từ Supabase
  useEffect(() => {
    console.log("Articles từ Supabase:", articles);
    console.log("Category Map:", categoryMap);
  }, [articles, categoryMap]);

  // Fallback map danh mục nếu không có categoryMap từ prop
  const fallbackCategoryMap: Record<string, string> = {
    "1": "Smart Living",
    "2": "Beauty",
    "3": "Health",
    "4": "Shop",
  };

  // Helper function để lấy tên danh mục dựa trên ID
  const getCategoryName = (categoryId: string) => {
    // Sử dụng category_name nếu có trong article
    if (
      articles.length > 0 &&
      "category_name" in articles[0] &&
      articles[0].category_name
    ) {
      // Phải cast article để TypeScript hiểu rằng chúng ta đang truy cập thuộc tính mở rộng
      const article = articles.find(
        (a) => a.category_id === categoryId
      ) as Article & { category_name?: string };
      if (article?.category_name) {
        return article.category_name;
      }
    }

    // Sử dụng categoryMap từ props nếu có
    if (categoryMap[categoryId]) {
      return categoryMap[categoryId];
    }

    // Fallback vào map cứng nếu không có
    return fallbackCategoryMap[categoryId] || "Other";
  };

  // Chuyển đổi dữ liệu từ Supabase sang format hiển thị
  const displayArticles =
    articles && articles.length > 0
      ? articles.slice(0, 6).map((article, index) => ({
          image:
            article.image_url ||
            "https://images.unsplash.com/photo-1596178065887-1198b6148b2b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
          category: getCategoryName(article.category_id),
          title: article.title,
          excerpt: article.excerpt,
          path: `/article?id=${article.id}`,
          delay: 0.1 * (index + 1),
        }))
      : [];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 w-full max-w-full">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-navy">
            Featured Articles
          </h2>
          <p className="mt-4 text-lg text-navy/70 max-w-2xl mx-auto">
            Discover our most popular content curated to inspire and elevate
            your lifestyle
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <div
                key={index}
                className="rounded-xl overflow-hidden glass-card"
              >
                <div className="animate-pulse">
                  <div className="aspect-[16/9] bg-navy/5"></div>
                  <div className="p-6">
                    <div className="h-4 w-16 bg-navy/10 rounded mb-3"></div>
                    <div className="h-6 bg-navy/10 rounded mb-2"></div>
                    <div className="h-4 bg-navy/10 rounded mb-1"></div>
                    <div className="h-4 bg-navy/10 rounded mb-4 w-2/3"></div>
                    <div className="h-4 w-24 bg-navy/10 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayArticles.map((article, index) => (
              <ArticleCard key={index} {...article} />
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/articles">
            <Button className="bg-[#1A2B4D] hover:bg-[#2E3F66] text-white px-8 py-6 h-auto text-lg shadow-lg hover:shadow-xl transition-all duration-300 group border border-transparent hover:border-gold">
              <span>View All Articles</span>
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedArticles;
